import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Linkedin, Instagram, Mail, MapPin } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';

const Footer = () => {
  const [legalPages, setLegalPages] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    fetchLegalPages();
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-settings`);
      if (response.ok) {
        const data = await response.json();
        setSiteSettings(data);
      }
      setSettingsLoaded(true);
    } catch (error) {
      console.error('Error fetching site settings:', error);
      setSettingsLoaded(true);
    }
  };

  const fetchLegalPages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/legal-pages`);
      if (response.ok) {
        const data = await response.json();
        setLegalPages(data.filter(page => page.is_published));
      }
    } catch (error) {
      console.error('Error fetching legal pages:', error);
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Company Info Header */}
        <div className="mb-8 pb-8 border-b border-gray-800">
          <picture>
            <source srcSet="/logo.webp" type="image/webp" />
            <img src="/logo.png" alt="Credlocity" className="h-10 w-auto mb-4 brightness-0 invert" width="194" height="70" />
          </picture>
          <p className="text-gray-300 mb-4">{t('footer.tagline')}</p>
          <div className="space-y-2 text-gray-400">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <a href="mailto:Admin@credlocity.com" className="hover:text-white transition">Admin@credlocity.com</a>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4" />
              <span>1500 Chestnut Street, Suite 2, Philadelphia, PA 19102</span>
            </div>
            <p className="text-sm">Credlocity Business Group LLC, formerly Ficostar Credit Services</p>
            <p className="text-sm italic">Not affiliated with FICO®. FICO® is a trademark of Fair Isaac Corporation.</p>
          </div>
        </div>

        {/* Four Column Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Column 1: Credit Education */}
          <div>
            <h3 className="font-cinzel font-semibold text-lg mb-4">{t('footer.credit_education')}</h3>
            <ul className="space-y-2">
              <li><Link to="/credit-scores" className="text-gray-400 hover:text-white transition text-sm">{t('footer.credit_scores')}</Link></li>
              <li><Link to="/credit-reports" className="text-gray-400 hover:text-white transition text-sm">{t('footer.credit_reports')}</Link></li>
              <li><Link to="/repair-methods" className="text-gray-400 hover:text-white transition text-sm">{t('footer.repair_methods')}</Link></li>
              <li><Link to="/debt-management" className="text-gray-400 hover:text-white transition text-sm">{t('footer.debt_management')}</Link></li>
              <li><Link to="/credit-building" className="text-gray-400 hover:text-white transition text-sm">{t('footer.credit_building')}</Link></li>
              <li><Link to="/financial-wellness" className="text-gray-400 hover:text-white transition text-sm">{t('footer.financial_wellness')}</Link></li>
              <li><Link to="/credit-repair-laws" className="text-gray-400 hover:text-white transition text-sm">{t('footer.education_hub')}</Link></li>
              <li><Link to="/fcra-guide" className="text-gray-400 hover:text-white transition text-sm">{t('footer.fcra_guide')}</Link></li>
              <li className="pl-3"><Link to="/fcra-605b-block" className="text-gray-400 hover:text-white transition text-sm">{t('footer.fcra_605b')}</Link></li>
              <li className="pl-6"><Link to="/fraud-removal" className="text-gray-400 hover:text-white transition text-sm">Fraud Removal (605B)</Link></li>
              <li><Link to="/fdcpa-guide" className="text-gray-400 hover:text-white transition text-sm">{t('footer.fdcpa_guide')}</Link></li>
              <li className="pl-3"><Link to="/collection-removal" className="text-gray-400 hover:text-white transition text-sm">Collection Removal</Link></li>
              <li className="pl-3"><Link to="/how-to-dispute-collections" className="text-gray-400 hover:text-white transition text-sm font-medium">How to Dispute Collections</Link></li>
              <li><Link to="/croa-guide" className="text-gray-400 hover:text-white transition text-sm">{t('footer.croa_guide')}</Link></li>
              <li className="pl-3"><Link to="/report-croa-violation" className="text-red-400 hover:text-red-300 transition text-sm font-medium">Report CROA Violation</Link></li>
              <li><Link to="/tsr-compliance" className="text-gray-400 hover:text-white transition text-sm">{t('footer.tsr_compliance')}</Link></li>
              <li><Link to="/late-payment-removal" className="text-gray-400 hover:text-white transition text-sm">Late Payment Removal</Link></li>
              <li><Link to="/human-trafficking-credit-block" className="text-gray-400 hover:text-white transition text-sm">Human Trafficking Credit Block</Link></li>
              <li><Link to="/credit-repair-scams" className="text-gray-400 hover:text-white transition text-sm">Credit Repair Scams</Link></li>
              <li><Link to="/store" className="text-gray-400 hover:text-white transition text-sm">Credlocity Store</Link></li>
            </ul>
          </div>

          {/* Column 2: Credlocity vs Competitors */}
          <div>
            <h3 className="font-cinzel font-semibold text-lg mb-4">{t('footer.vs_competitors')}</h3>
            <ul className="space-y-2">
              <li><Link to="/credit-repair-reviews" className="text-gray-400 hover:text-white transition text-sm font-medium">{t('footer.credit_repair_reviews')}</Link></li>
              <li><Link to="/why-us" className="text-gray-400 hover:text-white transition text-sm">{t('footer.why_us_over_them')}</Link></li>
              <li><Link to="/vs-lexington-law" className="text-gray-400 hover:text-white transition text-sm">vs Lexington Law</Link></li>
              <li><Link to="/vs-creditrepair" className="text-gray-400 hover:text-white transition text-sm">vs Creditrepair.com</Link></li>
              <li><Link to="/vs-credit-people" className="text-gray-400 hover:text-white transition text-sm">vs The Credit People</Link></li>
              <li><Link to="/vs-credit-pros" className="text-gray-400 hover:text-white transition text-sm">vs The Credit Pros</Link></li>
              <li><Link to="/vs-credit-saint" className="text-gray-400 hover:text-white transition text-sm">vs Credit Saint</Link></li>
              <li><Link to="/vs-white-jacobs" className="text-gray-400 hover:text-white transition text-sm">vs White Jacobs</Link></li>
            </ul>
          </div>

          {/* Column 3: Credlocity */}
          <div>
            <h3 className="font-cinzel font-semibold text-lg mb-4">{t('footer.credlocity')}</h3>
            <ul className="space-y-2">
              <li><Link to="/about-credlocity" className="text-gray-400 hover:text-white transition text-sm font-medium">About Credlocity</Link></li>
              <li><Link to="/success-stories" className="text-gray-400 hover:text-white transition text-sm">{t('footer.success_stories')}</Link></li>
              <li><Link to="/free-letters" className="text-gray-400 hover:text-white transition text-sm">Free Downloadable Letters</Link></li>
              <li><Link to="/free-credit-report-review" className="text-gray-400 hover:text-white transition text-sm font-medium">Free Credit Report Review</Link></li>
              <li><Link to="/609-dispute-letter" className="text-gray-400 hover:text-white transition text-sm">609 Dispute Letter Guide</Link></li>
              <li><Link to="/lawsuits" className="text-gray-400 hover:text-white transition text-sm">{t('footer.lawsuits_filed')}</Link></li>
              <li><Link to="/press-releases" className="text-gray-400 hover:text-white transition text-sm">{t('footer.press_releases')}</Link></li>
              
              {/* Dynamic Legal Pages */}
              {legalPages.map(page => (
                <li key={page.id}>
                  <Link to={`/legal/${page.slug}`} className="text-gray-400 hover:text-white transition text-sm">
                    {page.title}
                  </Link>
                </li>
              ))}
              
              <li><Link to="/partners" className="text-gray-400 hover:text-white transition text-sm">{t('footer.credlocity_partners')}</Link></li>
              <li><Link to="/become-a-partner" className="text-gray-400 hover:text-white transition text-sm">{t('footer.become_partner')}</Link></li>
              <li><Link to="/outsourcing" className="text-gray-400 hover:text-white transition text-sm">{t('footer.outsourcing_services')}</Link></li>
              <li><Link to="/credit-builder-store" className="text-gray-400 hover:text-white transition text-sm">Credit Builder Accounts</Link></li>
              <li><Link to="/affiliate-partners" className="text-gray-400 hover:text-white transition text-sm">Affiliate Partners</Link></li>
              <li><Link to="/attorney-marketplace" className="text-gray-400 hover:text-white transition text-sm font-medium">Attorney Marketplace</Link></li>
              <li><Link to="/cro-marketplace" className="text-gray-400 hover:text-white transition text-sm font-medium">CRO Partnership</Link></li>
              <li><Link to="/school" className="text-gray-400 hover:text-white transition text-sm" data-testid="footer-school-link">CROA Ethics School</Link></li>
            </ul>
            
            {/* Locations */}
            <div className="mt-6" data-testid="footer-locations-heading">
              <Link to="/locations" className="text-gray-300 hover:text-white transition font-semibold text-sm flex items-center gap-1">
                Locations <span className="text-xs text-gray-400">- View All</span>
              </Link>
            </div>
          </div>

          {/* Column 4: Credit Tools */}
          <div>
            <h3 className="font-cinzel font-semibold text-lg mb-4">{t('footer.credit_tools')}</h3>
            <ul className="space-y-2">
              <li><Link to="/tools/credit-score-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.credit_score_calc')}</Link></li>
              <li><Link to="/tools/debt-to-income-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.dti_calc')}</Link></li>
              <li><Link to="/tools/credit-utilization-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.utilization_calc')}</Link></li>
              <li><Link to="/tools/loan-payment-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.loan_calc')}</Link></li>
              <li><Link to="/tools/debt-payoff-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.debt_payoff_calc')}</Link></li>
              <li><Link to="/tools/mortgage-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.mortgage_calc')}</Link></li>
              <li><Link to="/tools/credit-card-payoff-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.cc_payoff_calc')}</Link></li>
              <li><Link to="/tools/savings-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.savings_calc')}</Link></li>
              <li><Link to="/tools/interest-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.interest_calc')}</Link></li>
              <li><Link to="/tools/budget-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.budget_calc')}</Link></li>
              <li><Link to="/tools/refinance-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.refinance_calc')}</Link></li>
              <li><Link to="/tools/student-loan-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.student_loan_calc')}</Link></li>
              <li><Link to="/tools/auto-loan-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.auto_loan_calc')}</Link></li>
              <li><Link to="/tools/retirement-calculator" className="text-gray-400 hover:text-white transition text-sm">{t('footer.retirement_calc')}</Link></li>
            </ul>
          </div>
        </div>

        {/* Consumer Protection */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <h3 className="font-cinzel font-semibold text-lg mb-4">{t('footer.consumer_protection')}</h3>
          <div className="grid md:grid-cols-3 gap-6 text-sm">
            <div>
              <h4 className="font-semibold mb-2">{t('footer.report_fraud')}:</h4>
              <p className="text-gray-400">{t('footer.report_fraud_desc')}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">{t('footer.ftc_complaints')}:</h4>
              <p className="text-gray-400">
                <a href="https://ftc.gov/complaint" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">ftc.gov/complaint</a> or 1-877-FTC-HELP
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">{t('footer.unfair_treatment')}:</h4>
              <p className="text-gray-400">{t('footer.unfair_treatment_desc')}</p>
            </div>
          </div>
          <p className="text-green-400 font-semibold mt-4">{t('footer.honest_fair_legal')}</p>
        </div>

        {/* Important Disclosure with Bubble Styling */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <h3 className="font-cinzel font-semibold text-xl mb-6 text-center">{t('footer.important_disclosure')}</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-800 border-2 border-secondary-green rounded-2xl p-6">
              <h4 className="font-cinzel font-semibold text-white mb-3 text-lg">{t('footer.your_rights')}</h4>
              <p className="text-gray-300 text-sm leading-relaxed">{t('footer.your_rights_desc')}</p>
            </div>
            <div className="bg-gray-800 border-2 border-primary-blue rounded-2xl p-6">
              <h4 className="font-cinzel font-semibold text-white mb-3 text-lg">{t('footer.requirements')}</h4>
              <p className="text-gray-300 text-sm leading-relaxed">{t('footer.requirements_desc')}</p>
            </div>
            <div className="bg-gray-800 border-2 border-secondary-green rounded-2xl p-6">
              <h4 className="font-cinzel font-semibold text-white mb-3 text-lg">{t('footer.tsr_compliance_title')}</h4>
              <p className="text-gray-300 text-sm leading-relaxed">
                {t('footer.tsr_compliance_desc')}{' '}
                <Link to="/tsr-compliance" className="text-green-400 hover:underline font-medium">
                  Read our full TSR & CROA Compliance Policy
                </Link>
              </p>
            </div>
            <div className="bg-gray-800 border-2 border-primary-blue rounded-2xl p-6">
              <h4 className="font-cinzel font-semibold text-white mb-3 text-lg">{t('footer.federal_notice')}</h4>
              <p className="text-gray-300 text-sm leading-relaxed">{t('footer.federal_notice_desc')}</p>
            </div>
            <div className="bg-gray-800 border-2 border-secondary-green rounded-2xl p-6">
              <h4 className="font-cinzel font-semibold text-white mb-3 text-lg">{t('footer.our_promise')}</h4>
              <p className="text-green-400 text-lg font-bold">{t('footer.honest_fair_legal')}</p>
              <p className="text-gray-300 text-sm mt-2">{t('footer.our_promise_desc')}</p>
            </div>
            <div className="bg-gray-800 border-2 border-yellow-500 rounded-2xl p-6">
              <h4 className="font-cinzel font-semibold text-white mb-3 text-lg">{t('footer.report_fraud_title')}</h4>
              <p className="text-gray-300 text-sm mb-3">{t('footer.report_fraud_question')}</p>
              <Link 
                to="/report-company" 
                className="inline-block bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-4 py-2 rounded-lg transition text-sm"
              >
                {t('footer.submit_complaint')}
              </Link>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="flex justify-center space-x-6 mb-8">
          {(!settingsLoaded || siteSettings?.social_profiles?.facebook) && (
            <a
              href={siteSettings?.social_profiles?.facebook || '#'}
              target={siteSettings?.social_profiles?.facebook ? '_blank' : '_self'}
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition"
              aria-label="Facebook"
            >
              <Facebook className="w-6 h-6" />
            </a>
          )}
          {(!settingsLoaded || siteSettings?.social_profiles?.twitter) && (
            <a
              href={siteSettings?.social_profiles?.twitter || '#'}
              target={siteSettings?.social_profiles?.twitter ? '_blank' : '_self'}
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition"
              aria-label="Twitter"
            >
              <Twitter className="w-6 h-6" />
            </a>
          )}
          {(!settingsLoaded || siteSettings?.social_profiles?.linkedin) && (
            <a
              href={siteSettings?.social_profiles?.linkedin || '#'}
              target={siteSettings?.social_profiles?.linkedin ? '_blank' : '_self'}
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-6 h-6" />
            </a>
          )}
          {(!settingsLoaded || siteSettings?.social_profiles?.instagram) && (
            <a
              href={siteSettings?.social_profiles?.instagram || '#'}
              target={siteSettings?.social_profiles?.instagram ? '_blank' : '_self'}
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition"
              aria-label="Instagram"
            >
              <Instagram className="w-6 h-6" />
            </a>
          )}
          {(!settingsLoaded || siteSettings?.social_profiles?.youtube) && (
            <a
              href={siteSettings?.social_profiles?.youtube || '#'}
              target={siteSettings?.social_profiles?.youtube ? '_blank' : '_self'}
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition"
              aria-label="YouTube"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            </a>
          )}
        </div>

        {/* Copyright */}
        {/* Consumer Resources Links */}
        <div className="flex flex-wrap justify-center gap-4 mb-6 text-sm">
          <a href="https://www.consumerfinance.gov" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition">CFPB</a>
          <span className="text-gray-700">•</span>
          <a href="https://www.ftc.gov" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition">FTC</a>
          <span className="text-gray-700">•</span>
          <a href="https://www.annualcreditreport.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition">AnnualCreditReport.com</a>
          <span className="text-gray-700">•</span>
          <a href="https://www.usa.gov/credit-reports" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition">USA.gov Credit Resources</a>
        </div>

        <div className="border-t border-gray-800 pt-8 text-center">
          <p className="text-gray-400 text-sm mb-2">
            © {t('footer.copyright')}
          </p>
          <p className="text-gray-400 text-sm">
            {t('footer.serving')}
          </p>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-700 text-center">
          <p className="text-xs text-gray-400 mb-1 uppercase tracking-wider font-medium">Consumer Transparency</p>
          <Link
            to="/complaint-history"
            className="inline-block text-xs text-gray-300 hover:text-white transition-colors underline-offset-2 hover:underline cursor-pointer"
          >
            Complaints against Credlocity in 17 years of business: <span className="text-white font-semibold">1 in BBB</span> · <span className="text-white font-semibold">2 in Google Reviews</span>
          </Link>
          <p className="text-[10px] text-gray-500 mt-1">We do not hide negative reviews. We believe consumers deserve full transparency to make informed decisions.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
